import React, { useEffect, useState } from "react"
import Client from "../../models/client"
import { FormData, ReportForm, ReportType } from "./ReportForm"
import { getReportPeriodLimit, getReportsSummary, readCookie } from "../api"
import useSWR from "swr"
import { ReportData, ReportSummaryTable } from "./ReportSummaryTable"
import { Box, Group, Text, ThemeIcon, Title } from "@mantine/core"
import { ChartLineIcon } from "@phosphor-icons/react"

// 上限を取得できなかった時の暫定値。
// 実際の上限より緩い値だと送信後にサーバ側で弾かれて理由がわからなくなるため短いほうにする。
const FALLBACK_PERIOD_LIMIT_MONTHS = 1

const DEFAULT_SUMMARY_ERROR = '集計に失敗しました。時間をおいて再度お試しください'
const DEFAULT_DOWNLOAD_ERROR = 'ダウンロードに失敗しました。時間をおいて再度お試しください。'

// 期間超過（400）や同時実行（409）はサーバーがそのまま表示できる文言を text/plain で返す。
// JSONやHTMLの本文をそのまま出しても読めないため汎用文言に置き換える。
// 401はここに来ない。axiosのインターセプタ（api/index.ts）がログイン画面へ飛ばすため。
const summaryErrorMessageFrom = (e: any): string => {
  const contentType = e?.response?.headers?.['content-type'] || ''
  if (!contentType.startsWith('text/plain')) {
    return DEFAULT_SUMMARY_ERROR
  }
  return e.response.data || DEFAULT_SUMMARY_ERROR
}

// ダウンロードは fetch のためインターセプタを通らず、401も自前で扱う
const downloadErrorMessageFrom = async (response: Response): Promise<string> => {
  if (response.status === 401) {
    return 'セッションの有効期限が切れました。再度ログインしてください。'
  }
  const contentType = response.headers.get('Content-Type') || ''
  if (!contentType.startsWith('text/plain')) {
    return DEFAULT_DOWNLOAD_ERROR
  }
  return (await response.text()) || DEFAULT_DOWNLOAD_ERROR
}

const filenameFromResponse = (response: Response): string | null => {
  const disposition = response.headers.get('Content-Disposition')
  if (!disposition) {
    return null
  }
  const matched = disposition.match(/filename="?([^"]+)"?/)
  return matched ? matched[1] : null
}

const saveBlob = (blob: Blob, filename: string) => {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  // click直後に破棄するとダウンロード開始前にURLが無効になるブラウザがあるため遅らせる
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}

interface Props {
  client: Client
}

const Report: React.FC<Props> = ({ client }) => {
  const [params, setParams] = useState<{
    reportType: ReportType,
    startDate: string,
    endDate: string,
  }>({
    reportType: 'cv',
    startDate: '',
    endDate: '',
  })
  const [periodLimitMonths, setPeriodLimitMonths] = useState<number | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getReportPeriodLimit(client.id)
      .then((months) => {
        if (!cancelled) setPeriodLimitMonths(months)
      })
      .catch(() => {
        if (!cancelled) setPeriodLimitMonths(FALLBACK_PERIOD_LIMIT_MONTHS)
      })
    return () => {
      cancelled = true
    }
  }, [client.id])

  const shouldFetch = Boolean(params.startDate && params.endDate)
  const { data, error, isValidating } = useSWR<ReportData>(
    shouldFetch
      ? (['reportSummary', client.id, params.startDate, params.endDate] as const)
      : null,
    async ([, clientId, startDate, endDate]) => {
      const res = await getReportsSummary(clientId as number, startDate as string, endDate as string)
      return {
        columns: res.data_columns.map((c) => ({
          headerName: c.Header,
          field: c.accessor,
        })),
        rows: res.data,
        sum: res.sum,
      }
    },
    // 集計は重いため、失敗時にSWRの自動リトライで再実行させない
    { revalidateOnFocus: false, shouldRetryOnError: false },
  )

  const handleSubmit = (formValues: FormData) => {
    setDownloadError(null)
    setParams({
      ...formValues,
      startDate: formValues.startDate ?? '',
      endDate: formValues.endDate ?? '',
    })
  }

  // ページ遷移ではなく fetch でダウンロードする。
  // 遷移方式では期間超過（400）や同時実行（409）のときにプレーンテキストのページへ飛んでしまう。
  const handleDownload = async () => {
    if (!params.startDate || !params.endDate || isDownloading) return

    const token = readCookie('refresh_token')
    const query = new URLSearchParams({
      from: params.startDate,
      to: params.endDate,
      ...(token ? { ref: btoa(token) } : {}),
    })
    const url = `/admin/api/clients/${client.id}/reports/${params.reportType}.csv?${query.toString()}`

    setIsDownloading(true)
    try {
      const response = await fetch(url)
      if (!response.ok) {
        setDownloadError(await downloadErrorMessageFrom(response))
        return
      }
      const fallbackName = `${params.reportType}_${client.url_name}_${params.startDate}_${params.endDate}.csv`
      saveBlob(await response.blob(), filenameFromResponse(response) || fallbackName)
      setDownloadError(null)
    } catch {
      setDownloadError(DEFAULT_DOWNLOAD_ERROR)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Box>
      <Group gap={12} mb={16}>
        <ThemeIcon variant="transparent">
          <ChartLineIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">サマリー</Title>
      </Group>

      <Text fz={14} c="#000000CC">
        紹介ページおよびゲストページから取得した各情報を確認できます。
      </Text>

      <ReportForm
        onSubmit={handleSubmit}
        periodLimitMonths={periodLimitMonths}
        isSubmitting={isValidating}
        submitError={error ? summaryErrorMessageFrom(error) : null}
      />
      {data && (
        <ReportSummaryTable
          data={data}
          onDownload={handleDownload}
          isDownloading={isDownloading}
          downloadError={downloadError}
        />
      )}
    </Box>
  )
}

export default Report
